export type StaffingPlanOrganizationReference = { id?: string; planItemKey?: string };

export type StaffingPlanOrganizationChange = {
  key: string;
  operation: 'CREATE' | 'CHANGE' | 'END';
  organizationId?: string;
  organizationDescriptor?: string;
  name?: string;
  parent?: StaffingPlanOrganizationReference;
  managerId?: string | null;
};

export type StaffingPlanWorkerChange = {
  key: string;
  workerId: string;
  workerDescriptor?: string;
  operation: 'JOB_CHANGE';
  organization?: StaffingPlanOrganizationReference;
  managerId?: string;
  clearManager?: boolean;
  organizationAssignments?: { primaryOrganization: StaffingPlanOrganizationReference; additionalOrganizations: StaffingPlanOrganizationReference[] };
  jobProfileId?: string;
  jobLevelId?: string;
  jobTypeId?: string;
  locationId?: string;
  timeType?: '' | 'FULL_TIME' | 'PART_TIME';
  workSpaceId?: string;
  clearWorkSpace?: boolean;
  businessTitle?: string;
  businessTitleMode?: 'KEEP' | 'CHANGE' | 'CLEAR';
};

export type StaffingPlanInput = {
  title: string;
  effectiveDate: string;
  reason: string;
  organizationChanges: StaffingPlanOrganizationChange[];
  workerChanges: StaffingPlanWorkerChange[];
};

export type StaffingPlan = StaffingPlanInput & {
  id: string;
  version: number;
  status: 'DRAFT' | 'SUBMITTED' | 'COMPLETED' | 'DENIED' | 'CANCELED';
  businessProcessId?: string;
  workflowId?: string;
  createdAt: string;
  updatedAt: string;
};

export type StaffingPlanValidation = {
  id: string;
  version: number;
  valid: boolean;
  issues: Array<{ key?: string; field?: string; code: string; message: string }>;
};

export const staffingPlanOrganizationLabels = { CREATE: '조직 신설', CHANGE: '조직 변경', END: '조직 종료' } as const;
export const staffingPlanWorkerLabels = { JOB_CHANGE: '조직·직무 변경' } as const;

export function staffingPlanInput(plan: StaffingPlanInput): StaffingPlanInput {
  return {
    title: plan.title.trim(), effectiveDate: plan.effectiveDate, reason: plan.reason.trim(),
    organizationChanges: plan.organizationChanges.map((row) => ({ ...row, name: row.name?.trim() || undefined })),
    workerChanges: plan.workerChanges.map(({ businessTitleMode, ...row }) => ({ ...row, businessTitle: businessTitleMode === 'KEEP' ? undefined : businessTitleMode === 'CLEAR' ? '' : row.businessTitle?.trim() })),
  };
}

export function validateStaffingPlan(plan: StaffingPlanInput, basicsOnly = false): string | undefined {
  if (!plan.title.trim() || !plan.effectiveDate || !plan.reason.trim()) return '발령안 제목, 시행일과 사유를 입력해 주세요.';
  if (basicsOnly) return undefined;
  if (plan.organizationChanges.length + plan.workerChanges.length === 0) return '조직 변경 또는 구성원 발령을 하나 이상 추가해 주세요.';
  const keys = [...plan.organizationChanges, ...plan.workerChanges].map((row) => row.key);
  if (keys.some((key) => !key) || new Set(keys).size !== keys.length) return '변경 항목의 식별자가 중복되었습니다. 항목을 다시 추가해 주세요.';
  const createKeys = new Set(plan.organizationChanges.filter((row) => row.operation === 'CREATE').map((row) => row.key));
  const validReference = (reference?: StaffingPlanOrganizationReference) => !reference
    || (Boolean(reference.id) !== Boolean(reference.planItemKey) && (!reference.planItemKey || createKeys.has(reference.planItemKey)));
  const organizations = new Set<string>();
  for (const row of plan.organizationChanges) {
    if (row.managerId === '') return '지정할 조직장을 선택해 주세요.';
    if (row.operation === 'CREATE' && !row.name?.trim()) return '신설할 조직 이름을 입력해 주세요.';
    if (row.operation !== 'CREATE' && !row.organizationId) return '변경하거나 종료할 조직을 선택해 주세요.';
    if (row.organizationId && organizations.has(row.organizationId)) return '같은 조직은 발령안에 한 번만 추가할 수 있습니다.';
    if (row.organizationId) organizations.add(row.organizationId);
    if (!validReference(row.parent) || row.parent?.planItemKey === row.key || (row.organizationId && row.parent?.id === row.organizationId)) return '상위 조직을 다시 선택해 주세요.';
    if (row.operation === 'CHANGE' && !row.name?.trim() && !row.parent && row.managerId === undefined) return '조직 이름, 상위 조직 또는 조직장을 변경해 주세요.';
  }
  const parents = new Map(plan.organizationChanges.map((row) => [row.key, row.parent?.planItemKey]));
  for (const row of plan.organizationChanges) {
    const seen = new Set<string>();
    let key: string | undefined = row.key;
    while (key) {
      if (seen.has(key)) return '신설 조직의 상위 조직이 서로 순환하지 않게 선택해 주세요.';
      seen.add(key); key = parents.get(key);
    }
  }
  const workers = new Set<string>();
  for (const row of plan.workerChanges) {
    if (!row.workerId) return '대상 구성원을 선택해 주세요.';
    if (workers.has(row.workerId)) return '같은 구성원은 발령안에 한 번만 추가할 수 있습니다.';
    workers.add(row.workerId);
    if (row.workSpaceId !== undefined && !row.workSpaceId.trim() && !row.clearWorkSpace) return '업무 공간을 선택하거나 해제를 지정해 주세요.';
    if (row.workSpaceId && row.clearWorkSpace) return '업무 공간 변경과 해제를 함께 지정할 수 없습니다.';
    if (row.timeType !== undefined && !['FULL_TIME', 'PART_TIME'].includes(row.timeType)) return '근무 형태를 다시 선택해 주세요.';
    if (row.managerId !== undefined && !row.managerId.trim() && !row.clearManager) return '보고 관리자를 선택하거나 해제를 지정해 주세요.';
    if (row.managerId && row.clearManager) return '보고 관리자 지정과 해제를 함께 선택할 수 없습니다.';
    if (row.organizationAssignments && row.organization) return '주 소속 이동과 소속 전체 변경은 함께 지정할 수 없습니다.';
    if (row.organizationAssignments) {
      const refs = [row.organizationAssignments.primaryOrganization, ...row.organizationAssignments.additionalOrganizations];
      if (refs.some(ref => !validReference(ref) || !planOrganizationValue(ref))) return '소속 조직을 다시 선택해 주세요.';
      if (new Set(refs.map(planOrganizationValue)).size !== refs.length) return '같은 소속 조직을 중복 지정할 수 없습니다.';
    }
    if (!validReference(row.organization)) return '구성원의 이동 조직을 다시 선택해 주세요.';
    if (row.businessTitleMode === 'CHANGE' && !row.businessTitle?.trim()) return '변경할 직함을 입력하거나 직함 해제를 선택해 주세요.';
    if (row.operation === 'JOB_CHANGE' && !row.organization && !row.jobProfileId && !row.jobLevelId && !row.jobTypeId && !row.locationId && !row.timeType && !row.workSpaceId && !row.clearWorkSpace && !row.managerId && !row.clearManager && !row.organizationAssignments && row.businessTitle === undefined && row.businessTitleMode !== 'CLEAR') return '구성원별 변경 내용을 하나 이상 입력해 주세요.';
  }
  return undefined;
}

export function planOrganizationValue(reference?: StaffingPlanOrganizationReference): string {
  return reference?.planItemKey ? `plan:${reference.planItemKey}` : reference?.id ?? '';
}

export function planOrganizationReference(value: string): StaffingPlanOrganizationReference | undefined {
  return value.startsWith('plan:') ? { planItemKey: value.slice(5) } : value ? { id: value } : undefined;
}
